import React, { useState, useEffect } from 'react';
import { Icon } from '@iconify/react';
import {
  LoyaltyActiveSystem,
  LoyaltyParticipantItem,
  ManualAdjustmentPayload,
} from '@/types/loyalty';
import loyaltyService from '@/services/loyaltyService';
import showToast from '@/components/ui/Toast';
import { getApiErrorMessage } from '@/utils/apiErrorMessage';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  arenaSlug: string;
  system: LoyaltyActiveSystem;
  participant: LoyaltyParticipantItem | null;
  onSuccess: () => void;
}

export const ManualAdjustModal: React.FC<Props> = ({
  isOpen,
  onClose,
  arenaSlug,
  system,
  participant,
  onSuccess,
}) => {
  const [delta, setDelta] = useState<number>(0);
  const [reason, setReason] = useState<string>('');
  const [submitting, setSubmitting] = useState<boolean>(false);

  useEffect(() => {
    if (isOpen) {
      setDelta(system === 'membership' ? 1 : 0);
      setReason('');
    }
  }, [isOpen, system]);

  if (!isOpen || !participant) return null;

  const currentPoints = participant.points_balance || 0;
  const currentStamps = participant.stamp_current || 0;

  const resultingPoints = currentPoints + delta;
  const resultingStamps = currentStamps + delta;

  const canSubmit = () => {
    if (!reason.trim()) return false;
    if (system === 'points') {
      if (delta === 0) return false;
      if (resultingPoints < 0) return false;
    }
    if (system === 'stamp') {
      if (delta === 0) return false;
      if (resultingStamps < 0) return false;
    }
    if (system === 'membership') {
      if (delta === 0) return false;
    }
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!reason.trim()) {
      showToast.error('Alasan penyesuaian wajib diisi');
      return;
    }

    try {
      setSubmitting(true);
      const payload: ManualAdjustmentPayload = {
        customer_id: participant.customer_id,
        system,
        reason: reason.trim(),
      };

      if (system === 'points') {
        payload.points_delta = delta;
      } else if (system === 'stamp') {
        payload.stamps_delta = delta;
      } else if (system === 'membership') {
        payload.membership_months_delta = delta;
      }

      await loyaltyService.manualAdjustment(arenaSlug, payload);
      showToast.success('Penyesuaian manual berhasil disimpan');
      onSuccess();
      onClose();
    } catch (err) {
      showToast.error(getApiErrorMessage(err, 'Gagal menyimpan penyesuaian'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-[rgba(14,23,38,0.5)]">
      <div className="w-full max-w-[420px] rounded-[16px] bg-white border border-[#E0E6ED] shadow-[0_4px_12px_rgba(14,23,38,.12)] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 pt-6 pb-4 border-b border-[#E0E6ED]">
          <h2 className="text-[18px] font-bold text-[#0E1726]">
            {system === 'membership' ? 'Perpanjang Member' : 'Penyesuaian Manual'}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="flex h-8 w-8 items-center justify-center rounded-full text-[#8C8C8C] hover:bg-[#F6F6F6] hover:text-[#1F1F1F] transition-colors"
          >
            <Icon icon="ph:x" className="w-4 h-4" />
          </button>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          {/* Participant Info Card */}
          <div className="rounded-[12px] bg-[#F6F6F6] border border-[#E0E6ED] p-4 flex items-center justify-between">
            <div>
              <span className="text-[12px] font-semibold uppercase tracking-[0.8px] text-[#8C8C8C]">
                Pelanggan
              </span>
              <h5 className="text-[14px] font-semibold text-[#0E1726]">{participant.name}</h5>
              <span className="text-[12px] text-[#8C8C8C] font-mono">
                {participant.formatted_phone || participant.phone || '-'}
              </span>
            </div>
            <div className="text-right">
              <span className="text-[12px] font-semibold uppercase tracking-[0.8px] text-[#8C8C8C]">
                {system === 'points'
                  ? 'Saldo Saat Ini'
                  : system === 'stamp'
                  ? 'Stamp Saat Ini'
                  : 'Status'}
              </span>
              <div className="text-[14px] font-bold text-[#FC480D]">
                {system === 'points' && `${currentPoints} Poin`}
                {system === 'stamp' && `${currentStamps} Stamp`}
                {system === 'membership' && (participant.membership_end_date || 'Active')}
              </div>
            </div>
          </div>

          {/* Stepper Controls */}
          {system === 'points' && (
            <div className="space-y-2">
              <label className="text-[13px] font-semibold text-[#0E1726]">
                Perubahan Poin
              </label>
              <div className="flex items-center justify-center gap-4 py-2">
                <button
                  type="button"
                  disabled={resultingPoints <= 0}
                  onClick={() => setDelta((prev) => prev - 10)}
                  className="w-10 h-10 rounded-full border border-[#D5D5D5] bg-white text-[#1F1F1F] hover:bg-[#F6F6F6] flex items-center justify-center disabled:opacity-45"
                >
                  <Icon icon="ph:minus" className="w-4 h-4" />
                </button>
                <div className="text-center min-w-[120px]">
                  <span
                    className={`text-[24px] font-semibold font-['Inter'] tracking-[-0.4px] ${
                      delta > 0
                        ? 'text-[#088a2f]'
                        : delta < 0
                        ? 'text-[#E7515A]'
                        : 'text-[#8C8C8C]'
                    }`}
                  >
                    {delta > 0 ? `+${delta}` : delta}
                  </span>
                  <p className="text-[12px] text-[#8C8C8C] mt-0.5">
                    Saldo Akhir: <b className="text-[#0E1726]">{resultingPoints} poin</b>
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setDelta((prev) => prev + 10)}
                  className="w-10 h-10 rounded-full border border-[#D5D5D5] bg-white text-[#1F1F1F] hover:bg-[#F6F6F6] flex items-center justify-center"
                >
                  <Icon icon="ph:plus" className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}

          {system === 'stamp' && (
            <div className="space-y-2">
              <label className="text-[13px] font-semibold text-[#0E1726]">
                Perubahan Stamp
              </label>
              <div className="flex items-center justify-center gap-4 py-2">
                <button
                  type="button"
                  disabled={resultingStamps <= 0}
                  onClick={() => setDelta((prev) => prev - 1)}
                  className="w-10 h-10 rounded-full border border-[#D5D5D5] bg-white text-[#1F1F1F] hover:bg-[#F6F6F6] flex items-center justify-center disabled:opacity-45"
                >
                  <Icon icon="ph:minus" className="w-4 h-4" />
                </button>
                <div className="text-center min-w-[120px]">
                  <span
                    className={`text-[24px] font-semibold font-['Inter'] tracking-[-0.4px] ${
                      delta > 0
                        ? 'text-[#088a2f]'
                        : delta < 0
                        ? 'text-[#E7515A]'
                        : 'text-[#8C8C8C]'
                    }`}
                  >
                    {delta > 0 ? `+${delta}` : delta}
                  </span>
                  <p className="text-[12px] text-[#8C8C8C] mt-0.5">
                    Stamp Akhir: <b className="text-[#0E1726]">{resultingStamps} stamp</b>
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setDelta((prev) => prev + 1)}
                  className="w-10 h-10 rounded-full border border-[#D5D5D5] bg-white text-[#1F1F1F] hover:bg-[#F6F6F6] flex items-center justify-center"
                >
                  <Icon icon="ph:plus" className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}

          {system === 'membership' && (
            <div className="space-y-2">
              <label className="text-[13px] font-semibold text-[#0E1726]">
                Perpanjang Masa Aktif
              </label>
              <div className="flex flex-wrap gap-2">
                {[1, 3, 6, 12].map((m) => (
                  <button
                    key={m}
                    type="button"
                    onClick={() => setDelta(m)}
                    className={`h-10 px-4 rounded-full border text-[13px] font-semibold transition-colors ${
                      delta === m
                        ? 'border-[#FC480D] bg-[#FFF5EE] text-[#FC480D]'
                        : 'border-[#E0E6ED] bg-white text-[#1F1F1F] hover:bg-[#F6F6F6]'
                    }`}
                  >
                    +{m} Bulan
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Mandatory Reason */}
          <div className="space-y-1.5">
            <label className="text-[13px] font-semibold text-[#0E1726]">
              Alasan Penyesuaian <span className="text-[#E7515A]">*</span>
            </label>
            <textarea
              required
              rows={3}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Contoh: Kompensasi kendala booking / Bonus event"
              className="w-full rounded-[12px] border border-[#E0E6ED] bg-white p-3 text-[14px] text-[#1F1F1F] placeholder:text-[#8C8C8C] focus:border-[#FC480D] focus:outline-none resize-none"
            />
            <p className="text-[12px] text-[#8C8C8C] leading-[18px]">
              Alasan wajib dicatat ke dalam audit log aktivitas admin arena.
            </p>
          </div>

          {/* Footer Buttons */}
          <div className="flex items-center justify-end gap-2 pt-2 border-t border-[#E0E6ED]">
            <button
              type="button"
              disabled={submitting}
              onClick={onClose}
              className="h-10 px-[18px] rounded-full bg-[#FFF5EE] hover:bg-[#ffe8db] text-[#FC480D] text-[14px] font-semibold transition-colors"
            >
              Batal
            </button>
            <button
              type="submit"
              disabled={!canSubmit() || submitting}
              className="inline-flex items-center gap-1.5 h-10 px-[18px] rounded-full bg-[#FC480D] hover:bg-[#E8490F] text-white text-[14px] font-semibold transition-colors disabled:opacity-45"
            >
              {submitting && <Icon icon="ph:spinner" className="w-4 h-4 animate-spin" />}
              Simpan Penyesuaian
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ManualAdjustModal;
